import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaArrowLeft } from 'react-icons/fa';

const projectsData = {
  'healthcare-compliance-platform': {
    icon: '🏥',
    title: 'Healthcare Compliance Platform',
    subtitle: 'EBER Digital Logs',
    industry: 'Healthcare',
    role: 'Product Manager & Project Manager',
    productType: 'Compliance & Operations Platform',
    tags: ['Healthcare', 'Compliance', 'React'],
    overview: 'A healthcare operations platform designed to digitize and streamline clinical workflows for dialysis centers. The system replaces manual documentation with structured digital logs, enabling clinics to track treatment processes, ensure regulatory compliance, and improve operational efficiency. My involvement spanned the full product lifecycle — from defining product vision and managing delivery, to designing backend architecture, managing multi-environment deployments, and providing ongoing client support.',
    challenge: {
      intro: 'Dialysis clinics traditionally rely on paper-based documentation and fragmented systems to track treatment workflows, equipment logs, and compliance activities. This created several operational challenges:',
      points: [
        'Paper-based logs were difficult to maintain and audit.',
        'Compliance monitoring required constant manual supervision.',
        'Clinics lacked real-time visibility into operational activities.',
        'Critical procedures could be delayed without timely alerts.',
        'No structured deployment pipeline to manage multiple environments reliably.',
      ],
      objective: 'The objective was to build a platform that could digitize clinic operations, automate compliance monitoring, and support stable deployments across production and other environments.',
    },
    role_detail: {
      intro: 'I worked as both Product Manager and Project Manager, with an extended scope that included backend architecture design, deployment management, and client support. Responsibilities included:',
      points: [
        'Designing product workflows for clinical operations',
        'Defining feature requirements and specifications',
        'Managing project timelines and development sprints',
        'Coordinating between engineering teams and stakeholders',
        'Ensuring features aligned with healthcare compliance requirements',
        'Designing and documenting the backend system architecture',
        'Managing deployments across production, sales, and other environments',
        'Providing ongoing technical support to the client post-deployment',
      ],
    },
    solution: {
      intro: 'The platform introduced a configurable system that allows clinics to digitize operational workflows and track compliance activities, backed by a structured backend architecture and a reliable multi-environment deployment setup.',
      features: [
        { title: 'Digital Log System', description: 'Converted manual paper logs into structured digital forms for dialysis process documentation.' },
        { title: 'Compliance Alert Engine', description: 'Enabled clinics to configure automated rules that trigger alerts when critical procedures are not completed within defined timeframes.' },
        { title: 'Workflow Tracking', description: 'Mapped different treatment steps into structured digital workflows.' },
        { title: 'Operational Dashboard', description: 'Provided real-time insights into clinic operations and compliance status.' },
        { title: 'Backend Architecture Design', description: 'Designed the backend system architecture to support scalable data management, API integrations, and compliance data flows.' },
        { title: 'Multi-Environment Deployment', description: 'Managed and maintained separate deployment environments — production, sales, and development — ensuring stable and isolated operations across each.' },
        { title: 'Client Support', description: 'Provided hands-on technical support to the client, resolving operational issues and guiding them through platform usage post-launch.' },
      ],
    },
    impact: [
      'Reducing dependency on paper-based logs',
      'Improving compliance monitoring',
      'Providing real-time operational visibility',
      'Standardizing clinical workflows across clinics',
      'Enabling stable multi-environment deployments with clear separation of production and non-production systems',
      'Improving client confidence through responsive post-deployment support',
    ],
    contributions: [
      'Designed the Compliance Alert System for automated rule-based monitoring',
      'Defined Digital Log Workflow Architecture',
      'Led Sprint Planning and Delivery Coordination',
      'Designed Notification and Compliance Monitoring Framework',
      'Designed backend system architecture for scalable clinical data management',
      'Managed production, sales, and development environment deployments',
      'Provided ongoing client support and issue resolution post-launch',
      'Worked closely with engineering teams to translate clinical workflows into product features',
    ],
    tools: ['Product Management', 'Agile / Scrum', 'Jira', 'SQL', 'API Design', 'Backend Architecture', 'Workflow Architecture', 'Healthcare Compliance Systems', 'Deployment Management', 'Data Analytics & Reporting'],
  },

  /* BIOTRACK - commented out, content needs revision
  'specimen-tracking-platform': {
    icon: '🔬',
    title: 'Specimen Tracking Platform',
    subtitle: 'EBER BioTrack',
    industry: 'Healthcare / Laboratory Systems',
    role: 'Product Manager & Project Manager',
    productType: 'Laboratory Tracking System',
    tags: ['Healthcare', 'Laboratory', 'Compliance'],
    overview: 'A laboratory workflow system designed to track biological samples throughout the laboratory process. The platform improves specimen traceability and ensures accurate monitoring of sample movement across different processing stages.',
    challenge: {
      intro: 'Laboratories often process large volumes of biological samples, and without structured tracking systems, errors can occur in sample identification and processing. Common challenges included:',
      points: [
        'Limited visibility into specimen processing stages',
        'Risk of sample misidentification',
        'Difficulty maintaining traceability',
        'Inefficient laboratory workflows',
      ],
      objective: 'The goal was to develop a system that could ensure reliable specimen tracking and improve laboratory operational efficiency.',
    },
    role_detail: {
      intro: 'I served as Product Manager and Project Manager, responsible for defining product workflows and coordinating development execution. Responsibilities included:',
      points: [
        'Designing specimen tracking workflows',
        'Creating feature specifications and development tickets',
        'Managing development sprints',
        'Coordinating product delivery with engineering teams',
        'Ensuring system usability and operational reliability',
      ],
    },
    solution: {
      intro: 'The platform introduced structured specimen tracking and laboratory workflow management.',
      features: [
        { title: 'Specimen Tracking System', description: 'Enabled laboratories to track biological samples across each processing stage.' },
        { title: 'Workflow Monitoring', description: 'Provided visibility into sample status and processing progress.' },
        { title: 'Audit & Traceability', description: 'Maintained detailed records of sample history for compliance and auditing.' },
        { title: 'Operational Reporting', description: 'Provided insights into laboratory performance and workflow efficiency.' },
      ],
    },
    impact: [
      'Improve traceability of biological samples',
      'Reduce sample handling errors',
      'Increase operational transparency',
      'Streamline laboratory workflows',
    ],
    contributions: [
      'Designed Specimen Tracking Workflow System',
      'Defined Laboratory Process Mapping and Data Structures',
      'Led Product Planning and Sprint Execution',
      'Implemented Traceability and Audit Mechanisms',
      'Coordinated cross-team development and delivery',
    ],
    tools: ['Product Management', 'Agile / Scrum', 'Jira', 'SQL', 'Workflow System Design', 'API Integration', 'Healthcare Data Systems', 'Process Automation'],
  },
  */

  'devops-automation-platform': {
    icon: '🔗',
    title: 'Smart Dependency Management Platform',
    subtitle: 'Developer Security & Governance',
    industry: 'Developer Tools / Security',
    role: 'Project Manager & Deployment Lead',
    productType: 'Developer Governance Platform',
    tags: ['Automation', 'DevOps', 'Security'],
    overview: 'A developer-focused platform designed to help organizations manage repository governance, security monitoring, and development telemetry. The platform provides centralized visibility into repository activity and enables organizations to enforce security and governance policies across their development environments. In addition to project management, I led the deployment of the platform on DigitalOcean infrastructure.',
    challenge: {
      intro: 'Engineering teams often manage multiple repositories across different platforms, making it difficult to monitor development activity and enforce governance standards. Organizations required a solution that could:',
      points: [
        'Provide centralized repository visibility',
        'Track development telemetry',
        'Enforce governance and security policies',
        'Monitor platform usage across teams',
        'Be deployed on a reliable, scalable cloud infrastructure',
      ],
      objective: '',
    },
    role_detail: {
      intro: 'I worked as Project Manager and Deployment Lead, responsible for coordinating development execution, ensuring timely delivery, and managing the platform deployment on DigitalOcean. Responsibilities included:',
      points: [
        'Managing project timelines and sprint execution',
        'Creating development tickets and coordinating tasks',
        'Facilitating communication between engineering teams and stakeholders',
        'Tracking feature progress and delivery milestones',
        'Deploying and configuring the platform on DigitalOcean',
        'Setting up server environments and ensuring production stability',
      ],
    },
    solution: {
      intro: 'The platform introduced tools to manage repository governance, monitor development activity, and was deployed on DigitalOcean for reliable cloud hosting.',
      features: [
        { title: 'Repository Monitoring', description: 'Centralized tracking of repository activity and updates.' },
        { title: 'Telemetry System', description: 'Collected usage metrics including scans, repository updates, and development activity.' },
        { title: 'Governance Policies', description: 'Allowed organizations to enforce security policies across development workflows.' },
        { title: 'Subscription Architecture', description: 'Introduced scalable subscription tiers with usage tracking infrastructure.' },
        { title: 'DigitalOcean Deployment', description: 'Deployed and configured the platform on DigitalOcean, managing server setup, environment configuration, and production stability.' },
      ],
    },
    impact: [
      'Improve governance of development environments',
      'Monitor repository activity and usage',
      'Strengthen security compliance',
      'Scale development workflows across teams',
      'Deliver a stable cloud-hosted platform on DigitalOcean',
    ],
    contributions: [
      'Managed project planning and sprint coordination',
      'Defined development tickets and feature breakdowns',
      'Coordinated engineering team delivery',
      'Supported implementation of telemetry and governance features',
      'Led platform deployment on DigitalOcean infrastructure',
      'Configured server environments and ensured production stability',
      'Ensured project milestones and delivery timelines were met',
    ],
    tools: ['Project Management', 'Agile / Scrum', 'Jira', 'SQL', 'DigitalOcean', 'SaaS Platform Architecture', 'Telemetry Systems', 'Governance Frameworks', 'API Integration', 'Server Configuration'],
  },

  'enterprise-warehousing-solution': {
    icon: '🚀',
    title: 'Enterprise Warehousing Solution',
    subtitle: 'Application Deployment & Security',
    industry: 'Enterprise Software',
    role: 'Technical Advisor & Deployment Lead',
    productType: 'Enterprise Application Platform',
    tags: ['Enterprise', 'Cloud', 'Scalability'],
    overview: 'An enterprise application platform designed to support internal business workflows. My role focused on advising on technical implementation, deploying the application infrastructure, and ensuring secure and reliable system operations.',
    challenge: {
      intro: 'The organization required assistance with deploying and securing their application infrastructure to ensure reliable system operations. Key challenges included:',
      points: [
        'Ensuring secure deployment of the application',
        'Configuring infrastructure for stable operations',
        'Protecting the system from potential vulnerabilities',
        'Ensuring the application environment followed security best practices',
      ],
      objective: '',
    },
    role_detail: {
      intro: 'I worked as a Technical Advisor, supporting the team with deployment strategy and application security. Responsibilities included:',
      points: [
        'Advising on system architecture and deployment approach',
        'Deploying the application infrastructure',
        'Implementing security best practices',
        'Ensuring stable production environments',
      ],
    },
    solution: {
      intro: 'The engagement focused on ensuring a reliable and secure application environment.',
      features: [
        { title: 'Application Deployment', description: 'Configured and deployed the application environment.' },
        { title: 'Infrastructure Security', description: 'Implemented security practices to protect the system from vulnerabilities.' },
        { title: 'Environment Configuration', description: 'Set up infrastructure and server environments for stable operations.' },
        { title: 'Technical Advisory', description: 'Provided guidance on improving system reliability and security.' },
      ],
    },
    impact: [
      'Deploy their application infrastructure successfully',
      'Improve system security posture',
      'Ensure stable and reliable application operations',
    ],
    contributions: [
      'Led application deployment and infrastructure setup',
      'Implemented security best practices',
      'Provided technical advisory on system architecture',
      'Ensured production environment stability',
    ],
    tools: ['DevOps', 'Application Deployment', 'Server Configuration', 'Security Hardening', 'Infrastructure Management', 'Linux Environment', 'System Monitoring'],
  },
};

const allProjectsList = [
  { id: 'healthcare-compliance-platform', icon: '🏥', title: 'Healthcare Compliance Platform', description: 'A comprehensive healthcare solution designed to optimize dialysis workflows, compliance monitoring, and clinical technician support.' },
  // { id: 'specimen-tracking-platform', icon: '🔬', title: 'Specimen Tracking Platform', description: 'A laboratory workflow system designed to track biological samples throughout the laboratory process.' },
  { id: 'devops-automation-platform', icon: '🔗', title: 'Smart Dependency Management Platform', description: 'A platform that enables developers to safely upgrade dependencies without code breaks, with governance and security monitoring.' },
  { id: 'enterprise-warehousing-solution', icon: '🚀', title: 'Enterprise Warehousing Solution', description: 'Enterprise technology solution focused on scalability and future-ready architecture for business transformation.' },
];

const ProjectDetail = () => {
  const { id } = useParams();
  const project = projectsData[id];

  const otherProjects = allProjectsList.filter(p => p.id !== id).slice(0, 3);

  if (!project) {
    return (
      <div className="container">
        <div className="blog-post-not-found">
          <h2>Project not found</h2>
          <p>The project you're looking for doesn't exist.</p>
          <Link to="/projects" className="back-to-blog" style={{ marginTop: '1rem', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
            <FaArrowLeft /> Back to Projects
          </Link>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="container"
    >
      {/* Back Navigation */}
      <motion.div
        className="blog-back-nav"
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.2, duration: 0.5 }}
      >
        <Link to="/projects" className="back-to-blog">
          <FaArrowLeft /> Back to All Projects
        </Link>
      </motion.div>

      <div className="blog-post">
        {/* Header */}
        <div className="blog-post-header">
          <div className="blog-post-icon">{project.icon}</div>
          <div className="blog-post-meta">
            <span className="blog-post-date">{project.industry}</span>
            <span>•</span>
            <span className="blog-post-read-time">{project.role}</span>
          </div>
          <h1 className="blog-post-title">{project.title}</h1>
          <p className="blog-post-author">{project.subtitle} — {project.productType}</p>
        </div>

        {/* Content */}
        <div className="blog-post-content">
          <div className="blog-content-wrapper">

            {/* Overview */}
            <h2>Overview</h2>
            <p>{project.overview}</p>

            {/* The Challenge */}
            <h2>The Challenge</h2>
            <p>{project.challenge.intro}</p>
            <ul>
              {project.challenge.points.map((point, i) => (
                <li key={i}>{point}</li>
              ))}
            </ul>
            {project.challenge.objective && <p>{project.challenge.objective}</p>}

            {/* My Role */}
            <h2>My Role</h2>
            <p>{project.role_detail.intro}</p>
            <ul>
              {project.role_detail.points.map((point, i) => (
                <li key={i}>{point}</li>
              ))}
            </ul>

            {/* Solution */}
            <h2>Solution</h2>
            <p>{project.solution.intro}</p>
            {project.solution.features.map((feature, i) => (
              <div key={i} style={{ marginBottom: '1rem' }}>
                <p><strong>{feature.title}</strong></p>
                <p>{feature.description}</p>
              </div>
            ))}

            {/* Impact */}
            <h2>Impact</h2>
            <p>The platform helped by:</p>
            <ul>
              {project.impact.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>

            {/* Key Contributions */}
            <h2>Key Contributions</h2>
            <ul>
              {project.contributions.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>

          </div>
        </div>

        {/* Tools & Tags */}
        <div className="blog-post-footer">
          <div className="blog-post-tags">
            {project.tools.map((tool, i) => (
              <span key={i} className="tag">{tool}</span>
            ))}
          </div>
        </div>
      </div>

      {/* More Projects */}
      {otherProjects.length > 0 && (
        <motion.div
          className="more-blogs-section"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
        >
          <h2 className="more-blogs-title">More Projects</h2>
          <div className="more-blogs-grid">
            {otherProjects.map((p, index) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + index * 0.1, duration: 0.5 }}
              >
                <Link to={`/projects/${p.id}`} className="more-blog-card">
                  <div className="more-blog-icon">{p.icon}</div>
                  <div className="more-blog-content">
                    <h3>{p.title}</h3>
                    <p>{p.description}</p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
          <div className="view-all-blogs">
            <Link to="/projects" className="view-all-btn">
              View All Projects →
            </Link>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default ProjectDetail;
